import Box from "@mui/joy/Box";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemButton from "@mui/joy/ListItemButton";
import ListItemContent from "@mui/joy/ListItemContent";
import Typography from "@mui/joy/Typography";
import { memo, useEffect, useRef, useState } from "react";
import { Chip, Divider, Stack } from "@mui/joy";
import { PatientType } from "api/patientApi";
import { patientAtom } from "recoil/patientAtom";
import { useRecoilState } from "recoil";
import { getTimeDifference } from "logic/logics_common";
import PatientRemindDoneButton from "./PatientRemindDoneButton";
import { SidebarMenuType } from "components/sidebar/SidebarForPatient";
import { theme } from "../../styles/theme";
import PatientTaskUpdateRequiredEnableButton from "./PatientTaskUpdateRequiredEnableButton";
import { patientListMenuKey } from "../../recoil/menuAtom";

function PatientItem({
  title,
  patient,
  selected,
  setSelected,
  setCurrentMenu,
  isHighlight,
  menuKey,
}: {
  title?: string;
  patient: PatientType;
  selected?: string;
  setSelected?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setCurrentMenu?: React.Dispatch<React.SetStateAction<SidebarMenuType>>;
  isHighlight?: boolean;
  menuKey?: patientListMenuKey;
}) {
  const {
    patientId,
    nickname,
    isRemindRequired,
    dayNumber,
    expiryDaysLeft,
    doneAt,
    unreadMessageCount,
    lastMessageSentAt,
    isNew,
    benefit,
    totalUpdateCount,
    currentUpdateCount,
  } = patient;

  const isEducation = benefit === "코치 교육";

  const [patientState, setPatientState] = useRecoilState(patientAtom);

  const [contextMenuOpenPatientId, setContextMenuOpenPatientId] = useState<
    number | undefined
  >(undefined);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleGlobalEvent = (e: MouseEvent) => {
      const target = e.target as Node | null;
      const inside = !!(
        rootRef.current &&
        target &&
        rootRef.current.contains(target)
      );
      if (!inside) {
        setContextMenuOpenPatientId(undefined);
      }
    };
    window.addEventListener("click", handleGlobalEvent);
    window.addEventListener("contextmenu", handleGlobalEvent);
    return () => {
      window.removeEventListener("click", handleGlobalEvent);
      window.removeEventListener("contextmenu", handleGlobalEvent);
    };
  }, []);

  const setSidebarTaskList = () => {
    if (setCurrentMenu) {
      setCurrentMenu("task");
    }
  };

  return (
    <ListItemButton
      ref={rootRef}
      sx={{
        borderRadius: "8px",
        height: "56px",
        py: 0,
        px: "12px",
        alignItems: "center",
      }}
      key={`${title}${patientId}`}
      className="relative"
      onClick={() => {
        setContextMenuOpenPatientId(undefined);
        if (setSelected) {
          setSelected(`${title}_${patientId}`);

          if (patientId !== patientState?.patientId) {
            setSidebarTaskList();
          } else {
            setSidebarTaskList();
          }
          setPatientState({ ...patient });
        }
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        setContextMenuOpenPatientId(patientId);
      }}
      selected={selected === `${title}_${patientId}`}
      color={selected === `${title}_${patientId}` ? "primary" : undefined}
    >
      {contextMenuOpenPatientId === patientId && (
        <Box
          className="absolute z-[30] right-[0px] top-[50%]"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <List
            orientation="vertical"
            sx={{
              width: "max-content",
              overflow: "hidden",
              borderWidth: 1,
              borderColor: theme.vars.palette.Border.SuperWeak,
              backgroundColor: theme.vars.palette.Surface.Forward,
              borderRadius: "12px",
              boxShadow:
                "0 8px 24px -12px var(--mono-colors-with-opacity-pure-black-20, rgba(0, 0, 0, 0.20))",
              px: 0,
              py: 0,
              "--List-gap": 0,
              "--ListItem-paddingY": 0,
              "--ListItem-paddingX": 0,
            }}
          >
            <ListItem sx={{ p: 0 }}>
              <PatientTaskUpdateRequiredEnableButton
                patient={patient}
                onAfterAction={() => {
                  setContextMenuOpenPatientId(undefined);
                }}
              />
            </ListItem>

            {title === "리마인드 필요" && isRemindRequired && (
              <>
                <Divider />
                <ListItem sx={{ p: 0 }}>
                  <PatientRemindDoneButton
                    patientId={patientId}
                    onAfterAction={() => {
                      setContextMenuOpenPatientId(undefined);
                    }}
                  />
                </ListItem>
              </>
            )}
          </List>
        </Box>
      )}
      <ListItemContent>
        <Stack direction={"row"}>
          <Stack spacing={"2px"} sx={{ flex: 1, alignItems: "start" }}>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              sx={{ width: "100%" }}
            >
              <Stack direction={"row"} spacing="8px">
                <Typography
                  variant={"Caption"}
                  sx={{
                    px: 0,
                    color: theme.vars.palette.Content.Strong,
                    fontWeight: 500,
                  }}
                >{`${nickname}`}</Typography>
                {isEducation ? (
                  <Chip
                    size="sm"
                    variant="outlined"
                    sx={{
                      borderColor: theme.vars.palette.Content.Highlight,
                      color: theme.vars.palette.Content.Highlight,
                    }}
                  >
                    교육
                  </Chip>
                ) : (
                  isNew && (
                    <Chip
                      size="sm"
                      variant="outlined"
                      sx={{
                        borderColor:
                          theme.vars.palette.Content.HighlightWithAlert,
                        color: theme.vars.palette.Content.HighlightWithAlert,
                      }}
                    >
                      NEW
                    </Chip>
                  )
                )}
              </Stack>
              {menuKey === "patientList_taskOpenRequired" && (
                <Stack
                  direction={"row"}
                  sx={{
                    mr: "4px",
                    alignSelf: "center",
                  }}
                >
                  <Typography
                    variant={"Footnote"}
                    component="span"
                    sx={{
                      color:
                        (currentUpdateCount || 0) < (totalUpdateCount || 0)
                          ? theme.vars.palette.Content.Highlight
                          : theme.vars.palette.Content.Weak,
                      fontWeight:
                        (currentUpdateCount || 0) < (totalUpdateCount || 0)
                          ? 700
                          : 500,
                    }}
                  >
                    {`${currentUpdateCount || 0}/`}
                  </Typography>
                  <Typography
                    variant={"Footnote"}
                    component="span"
                    sx={{
                      color: theme.vars.palette.Content.Weak,
                      fontWeight: 500,
                    }}
                  >
                    {totalUpdateCount || 0}
                  </Typography>
                </Stack>
              )}
            </Stack>
            <Box>
              <Stack
                direction={"row"}
                divider={
                  <Typography
                    sx={{
                      px: 1,
                      color: theme.vars.palette.Content.Weak,
                      fontWeight: 500,
                    }}
                    variant={"Footnote"}
                  >
                    ·
                  </Typography>
                }
              >
                {(unreadMessageCount || 0) > 0 && lastMessageSentAt ? (
                  <Typography
                    variant={"Footnote"}
                    sx={{
                      color: theme.vars.palette.Content.Highlight,
                      fontWeight: 700,
                    }}
                  >
                    {getTimeDifference(lastMessageSentAt)} 받음
                  </Typography>
                ) : (
                  doneAt && (
                    <Typography
                      variant={"Footnote"}
                      sx={{
                        color: theme.vars.palette.Content.Weak,
                        fontWeight: 500,
                      }}
                    >
                      {getTimeDifference(doneAt)} 완료
                    </Typography>
                  )
                )}
                <Typography
                  variant={"Footnote"}
                  noWrap
                  sx={{
                    color: theme.vars.palette.Content.Weak,
                    fontWeight: 500,
                  }}
                >
                  {typeof expiryDaysLeft === "number" && expiryDaysLeft <= 20
                    ? expiryDaysLeft === 0
                      ? "오늘 만료"
                      : `${expiryDaysLeft}일 후 만료`
                    : `${dayNumber}일차`}
                </Typography>
              </Stack>
            </Box>
          </Stack>
          {(unreadMessageCount || 0) > 0 && (
            <Box
              sx={{
                mr: "4px",
                width: "8px",
                height: "8px",
                borderRadius: "10px",
                backgroundColor: theme.vars.palette.Content.Highlight,
                alignSelf: "center",
              }}
            />
          )}
        </Stack>
      </ListItemContent>
    </ListItemButton>
  );
}

export default memo(PatientItem);
